
import React, { useEffect } from 'react';
import { Check, ChevronDown, Smartphone } from 'lucide-react';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UseFormReturn } from 'react-hook-form';
import { TradingAccountOption } from '@/types/trader';
import { ConnectApiFormData } from './types';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { AccountTypeBadge } from '@/components/common/AccountTypeBadge';
import { useIsMobile } from '@/hooks/use-mobile';

interface TradingAccountSelectProps {
  form: UseFormReturn<ConnectApiFormData>;
  tradingAccounts: TradingAccountOption[];
  isLoading: boolean;
}

export function TradingAccountSelect({ 
  form, 
  tradingAccounts, 
  isLoading 
}: TradingAccountSelectProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Effect to notify if no accounts are found after loading completes
  useEffect(() => {
    if (!isLoading && tradingAccounts.length === 0) {
      toast({
        title: t('no_trading_accounts_found'),
        description: t('no_trading_accounts_available'),
        variant: "destructive"
      });
    }
  }, [isLoading, tradingAccounts.length, toast, t]);

  // Enhanced skeleton for mobile
  const renderLoadingSkeleton = () => (
    <div className="space-y-3">
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className={`${isMobile ? 'h-14' : 'h-12'} bg-gray-200 rounded-lg flex items-center px-4`}>
              <div className="flex-1 space-y-1">
                <div className="h-3 bg-gray-300 rounded w-3/4"></div>
                <div className="h-2 bg-gray-300 rounded w-1/2"></div>
              </div>
              <div className="w-16 h-6 bg-gray-300 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-500 text-center`}>
        Loading your trading accounts...
      </p>
    </div>
  );
  
  return (
    <FormField
      control={form.control}
      name="trading_account_id"
      render={({ field }) => (
        <FormItem className="space-y-3">
          <FormLabel className={`${isMobile ? 'text-sm' : 'text-sm'} font-medium text-gray-900 flex items-center gap-2`}>
            {isMobile && <Smartphone className="h-4 w-4" />}
            Select Trading Account
          </FormLabel>
          {isLoading ? renderLoadingSkeleton() : (
            <div className="space-y-2">
              <Select 
                onValueChange={field.onChange} 
                value={field.value}
                disabled={isLoading || tradingAccounts.length === 0}
              >
                <FormControl>
                  <SelectTrigger className={`${isMobile ? 'h-14 text-base' : 'h-12'} rounded-lg border-2 border-gray-300 bg-white hover:border-tradebrand-green focus:border-tradebrand-green focus:ring-tradebrand-green/20 transition-colors shadow-sm ${isMobile ? 'active:scale-98' : ''}`}>
                    <div className="flex items-center justify-between w-full">
                      <SelectValue 
                        placeholder={isMobile ? "👆 Tap to choose account" : "👆 Click here to choose your trading account"}
                        className="text-gray-900 font-medium placeholder:text-gray-900 data-[placeholder]:text-gray-900"
                      />
                      <ChevronDown className={`${isMobile ? 'h-5 w-5' : 'h-4 w-4'} text-gray-500`} />
                    </div>
                  </SelectTrigger>
                </FormControl>
                <SelectContent 
                  className={`z-50 bg-white border-gray-200 shadow-xl rounded-lg overflow-hidden ${isMobile ? 'max-h-[50vh]' : ''}`}
                  position="popper"
                  align="start"
                  sideOffset={8}
                >
                  {tradingAccounts.length > 0 ? (
                    tradingAccounts.map((account) => (
                      <SelectItem 
                        key={account.id} 
                        value={account.id.toString()}
                        className={`${isMobile ? 'py-4 px-4' : 'py-3 px-4'} border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-green-50 focus:bg-green-50 transition-colors ${isMobile ? 'active:bg-green-100' : ''}`}
                      >
                        <div className="flex items-center justify-between w-full gap-3">
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className={`font-semibold text-gray-900 ${isMobile ? 'text-base' : 'text-sm'} truncate`}>
                              {account.name}
                            </span>
                            <span className={`${isMobile ? 'text-sm' : 'text-xs'} text-gray-600`}>
                              Account #{account.accountNumber}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <AccountTypeBadge isLive={account.isLive} />
                            <div className="text-right">
                              <div className={`font-bold ${isMobile ? 'text-base' : 'text-sm'} text-gray-900`}>
                                {account.balance.toLocaleString('en-US', { 
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2 
                                })}
                              </div>
                              <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-gray-600 font-medium`}>
                                {account.currency || 'USD'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-accounts" disabled>
                      <span className={`text-gray-500 ${isMobile ? 'text-base' : 'text-sm'}`}>
                        No trading accounts found
                      </span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              
              {/* Enhanced help text with mobile optimization */}
              <div className={`flex items-start gap-2 ${isMobile ? 'text-xs' : 'text-xs'} text-gray-500`}>
                <span className="flex-shrink-0">💡</span>
                <span>
                  {isMobile 
                    ? "Tap to choose the account you want to connect" 
                    : "Choose the trading account you want to connect to this bot"
                  }
                </span>
              </div>

              {/* Account count indicator for mobile */}
              {isMobile && tradingAccounts.length > 0 && (
                <div className="text-center">
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                    {tradingAccounts.length} account{tradingAccounts.length > 1 ? 's' : ''} available
                  </span>
                </div>
              )}
            </div>
          )}
          <FormMessage className={`${isMobile ? 'text-xs' : 'text-xs'} text-red-600`} />
        </FormItem>
      )}
    />
  );
}
